// ═══════════════════════════════════════════════════════════
// Holidays — per-jurisdiction holiday calendar
// Used by the statutory clock to skip non-business days.
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, deleteRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const F = FIELDS.holidays;

export interface Holiday {
  recordId:       number;
  jurisdictionId: string;
  date:           string;   // YYYY-MM-DD (for recurring, MM-DD portion is what matters)
  name:           string;
  isRecurring:    boolean;  // same MM-DD repeats every year
}

function rowToHoliday(row: Record<string, unknown>): Holiday {
  return {
    recordId:       row['record_id'] as number,
    jurisdictionId: String(row[F.jurisdictionId] ?? ''),
    date:           String(row[F.date] ?? ''),
    name:           String(row[F.name] ?? ''),
    isRecurring:    row[F.isRecurring] === true || row[F.isRecurring] === 'true',
  };
}

export async function listHolidays(jurisdictionId: string): Promise<Holiday[]> {
  const { records } = await queryRecords(TABLES.holidays, {
    filters: [{ field: F.jurisdictionId, operator: 'eq', value: jurisdictionId }],
    sort:    { field: F.date, direction: 'asc' },
    limit:   200,
  });
  return (records as Record<string, unknown>[]).map(rowToHoliday);
}

export async function createHoliday(input: {
  jurisdictionId: string;
  date:           string;
  name:           string;
  isRecurring:    boolean;
}): Promise<Holiday> {
  const row = await createRecord(TABLES.holidays, {
    [F.jurisdictionId]: input.jurisdictionId,
    [F.date]:           input.date,
    [F.name]:           input.name,
    [F.isRecurring]:    input.isRecurring,
  });
  return rowToHoliday(row as Record<string, unknown>);
}

export async function deleteHoliday(recordId: number): Promise<void> {
  await deleteRecord(TABLES.holidays, recordId);
}

/**
 * Build a Set of YYYY-MM-DD strings for clock calculation.
 * Recurring holidays are expanded ±2 years around today so deadline
 * math across year boundaries always works.
 */
export function buildHolidaySet(holidays: Holiday[]): Set<string> {
  const now = new Date().getFullYear();
  const set = new Set<string>();
  for (const h of holidays) {
    if (h.isRecurring) {
      const mmdd = h.date.slice(5); // extract MM-DD
      for (let y = now - 1; y <= now + 2; y++) set.add(`${y}-${mmdd}`);
    } else {
      set.add(h.date);
    }
  }
  return set;
}
